import { useState, useEffect, useRef, useCallback } from 'react';
import api, { ProductSuggestion } from '../services/api';

interface ProductAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onProductSelect?: (product: ProductSuggestion) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * Composant d'autocomplétion pour les noms de produits
 * Utilise l'API /products/suggest pour des suggestions fuzzy
 */
export default function ProductAutocomplete({
  value,
  onChange,
  onProductSelect,
  placeholder = 'Nom du produit...',
  className = '',
  disabled = false,
  autoFocus = false,
}: ProductAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<ProductSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounced search
  const searchProducts = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await api.suggestProducts(query, 8);
      setSuggestions(results);
      setShowDropdown(results.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error fetching product suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Debounce the search
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      searchProducts(newValue);
    }, 300);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectProduct(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle product selection
  const handleSelectProduct = (product: ProductSuggestion) => {
    onChange(product.name);
    setShowDropdown(false);
    setSelectedIndex(-1);
    if (onProductSelect) {
      onProductSelect(product);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-500 bg-gray-100';
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowDropdown(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${className}`}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* Dropdown de suggestions */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          <div className="p-2 text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
            {suggestions.length} produit{suggestions.length > 1 ? 's' : ''} trouvé{suggestions.length > 1 ? 's' : ''}
          </div>
          {suggestions.map((product, index) => (
            <div
              key={product.id}
              onClick={() => handleSelectProduct(product)}
              className={`px-3 py-2 cursor-pointer transition-colors ${
                index === selectedIndex
                  ? 'bg-emerald-50 border-l-2 border-emerald-500'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {product.name}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-0.5">
                    {product.code && (
                      <span className="bg-gray-100 px-1.5 py-0.5 rounded">
                        {product.code}
                      </span>
                    )}
                    {product.category_name && (
                      <span className="text-gray-400">{product.category_name}</span>
                    )}
                    {product.origin_country && (
                      <span className="text-gray-400">{product.origin_country}</span>
                    )}
                    {product.bio && (
                      <span className="text-green-600">Bio</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${getScoreColor(
                      product.similarity_score
                    )}`}
                  >
                    {product.similarity_score}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message quand pas de résultats */}
      {showDropdown && suggestions.length === 0 && value.trim().length >= 2 && !isLoading && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500"
        >
          <p className="text-sm">Aucun produit similaire trouvé</p>
          <p className="text-xs mt-1">Le produit sera créé comme nouveau</p>
        </div>
      )}
    </div>
  );
}
